import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateMemberRequest } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import type { MemberProfileApi } from './useMemberProfileApi';
import { MY_PROFILE_STATUS_KEY } from './useMemberProfileApi';
import { errorMessage } from './memberFormat';

/** Root key of the admin directory queries (invalidated after any member write). */
export const MEMBER_DIRECTORY_KEY = ['members', 'directory'] as const;

/** Invalidate everything that shows this member: the profile, the directory, the completion banner. */
export function useInvalidateMember(api: MemberProfileApi) {
  const queryClient = useQueryClient();
  return (what: 'all' | 'member' = 'member') => {
    if (what === 'all') queryClient.invalidateQueries({ queryKey: api.scope });
    else queryClient.invalidateQueries({ queryKey: api.keys.member });
    queryClient.invalidateQueries({ queryKey: MEMBER_DIRECTORY_KEY });
    if (api.mode === 'self') queryClient.invalidateQueries({ queryKey: MY_PROFILE_STATUS_KEY });
  };
}

/** Partial member update (null clears a field, omitted fields stay as they are). */
export function useMemberPatch(api: MemberProfileApi, successMessage = 'Your changes have been saved.') {
  const invalidate = useInvalidateMember(api);
  return useMutation({
    mutationFn: (patch: UpdateMemberRequest) => api.updateMember(patch),
    onSuccess: () => {
      toast.success('Saved', successMessage);
      invalidate();
    },
    onError: (e) => toast.error('Could not save', errorMessage(e)),
  });
}
